import { useOutletContext } from "react-router-dom";
import { Paper, FormControl, Alert } from '@mui/material';

import { notificationStore } from "~/stores/notification";
import Page from "~/components/Page";
import KeysTable from "~/tables/KeysTable";
import TableControls from "~/components/TableControls";
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectKeys, apiGetProjectApiKeyAvailableServices } from "~/providers/api";
import { IClientProject, IClientKey } from "~/providers/client-interfaces";
import { IAdminProject, IAdminKey } from "~/providers/admin-interfaces";
import { IOutlet, IPage } from "~/interfaces";
import RemoveKeyModal from "~/modals/RemoveKeyModal";
import CreateKeyModal from "~/modals/CreateKeyModal";
import { CLIENT_API_KEY, ADMIN_API_KEY, MAX_USER_KEYS } from "~/const";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import DisabledButtonTitle from "~/mui/DisabledButtonTitle";
import ExternalLink from "~/components/ExternalLink";
import { getProjectsAndKeysLink } from "~/utils/utils";
import { IServiceLittle } from "~/providers/interfaces";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	isVisitor: boolean;
	keys: Array<IClientKey | IAdminKey>;
	removeKey: IClientKey | IAdminKey;
	createKey: IClientKey | IAdminKey;
	projectLastUpdate: string;
	showCreateMsg: boolean;
	maxUserKeys: boolean;
	services: Array<IServiceLittle>;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	errorMsg: "",
	project: null,
	isVisitor: true,
	keys: [],
	removeKey: null,
	createKey: null,
	projectLastUpdate: "",
	showCreateMsg: false,
	maxUserKeys: false,
	services: [],
};

export default function ProjectKeysPage({
	isInternal = false,
}: IPage) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { setNotification } = notificationStore(notificationState => ({
		setNotification: notificationState.setNotification,
	}));
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();

	function addApiKey() {
		updateState({
			createKey: isInternal ? ADMIN_API_KEY : CLIENT_API_KEY,
		});
	}

	function keyCopy(apiKey: string) {
		navigator.clipboard.writeText(apiKey);
		setNotification(trans("keyCopiedToClipboard"));
	}

	function keySettings(createKey: IClientKey | IAdminKey) {
		updateState({
			createKey,
		});
	}

	function onKeyRemove(projectLastUpdate: string) {
		setState(prev => ({
			...prev,
			keys: prev.keys.filter(item => item.id !== prev.removeKey.id),
			removeKey: null,
			errorMsg: "",
			projectLastUpdate,
		}));
	}

	function onKeyCreate(keyData: IClientKey | IAdminKey, projectLastUpdate: string) {
		setState(prev => {
			const keys = prev.keys;
			const filtered = keys.filter(key => key.id === keyData.id);

			if (filtered.length === 1) {
				Object.assign(filtered[0], keyData);
			} else {
				keys.push(keyData);
			}

			return {
				...prev,
				keys,
				projectLastUpdate,
				createKey: null,
				isDirty: false,
				showCreateMsg: true,
			};
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, keysData, servicesData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectKeys(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectApiKeyAvailableServices(isInternal ? "admin" : "client", params.projectId, lang),
		]);

		updateState({
			loading: false,
			...projectData.noErrors
				? {
					project: projectData.data,
					projectLastUpdate: projectData.data.lastUpdate,
					isVisitor: !isInternal && projectData.data.userRole === "visitor",
				}
				: {},
			...keysData.noErrors
				? {
					keys: keysData.data,
					maxUserKeys: keysData.data.length >= MAX_USER_KEYS,
				}
				: {},
			...servicesData.noErrors
				? {
					services: servicesData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...keysData.errors,
				...servicesData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page checkLogged={true} title={trans("apiKeysProject")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("apiKeysProject"))} checkInternalRole={isInternal ? "salesman" : "none"}
		onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.keys">
		<Paper elevation={3} className="projectKeysPage__paper">
			<TableControls className="projectKeysPage__tableControls">
				<div></div>
				<FormControl size="small" className="projectKeysPage__addForm">
					<DisabledButtonTitle tooltipTitle={trans("limit.user.keys", { count: MAX_USER_KEYS })} color="success" variant="contained" onClick={addApiKey}
						className="projectKeysPage__addBtn" disabled={!isInternal && state.isVisitor}>
						{ trans("projectKeysPage.addKey") }
					</DisabledButtonTitle>
				</FormControl>
			</TableControls>
			<KeysTable items={state.keys} loading={state.loading} isInternal={isInternal} isVisitor={state.isVisitor} onCopy={keyCopy} onRemove={keyData => updateState({ removeKey: keyData })} onSettings={keyData => keySettings(keyData)} />
			{ !isInternal && <Alert severity="info" className="margin-top-small">
				{ trans("projectKeysPage.info2") }&nbsp;<ExternalLink to={getProjectsAndKeysLink(lang)}>
					{ trans("projectKeysPage.info2.linkTitle") }
				</ExternalLink>
			</Alert> }
			{ !isInternal && state.showCreateMsg && <Alert severity="warning" className="margin-top-small">
				{ trans("projectKeysPage.afterCreate") }
			</Alert> }
		</Paper>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.removeKey && <RemoveKeyModal isInternal={isInternal} keyData={state.removeKey} projectLastUpdate={state.projectLastUpdate} params={params} onClose={() => updateState({ removeKey: null })}
			onSave={onKeyRemove} /> }
		{ state.createKey && <CreateKeyModal isInternal={isInternal} keyData={state.createKey} projectLastUpdate={state.projectLastUpdate} params={params} onClose={() => updateState({ createKey: null, isDirty: false })}
			onSave={onKeyCreate} onChange={isDirty => updateState({ isDirty })} projectServices={state.services} /> }
	</Page>;
}
